export type CalendarDateRange = { from: string; to: string };

function timezoneOffsetAt(timestamp: number, timezone: string): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).formatToParts(new Date(timestamp));
    const values = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
    return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), Number(values.hour), Number(values.minute), Number(values.second)) - timestamp;
  } catch {
    return 0;
  }
}

/** Returns the civil calendar date in the provider's timezone. */
export function calendarDateKey(value: string, timezone: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value.slice(0, 10);
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(timestamp));
    const values = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return value.slice(0, 10);
  }
}

export function providerToday(timezone: string, now = new Date()): string {
  return calendarDateKey(now.toISOString(), timezone);
}

/** Converts a local calendar boundary into UTC while respecting DST. */
export function localDateTimeToIso(value: string, timezone: string): string | undefined {
  const civil = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(civil)) return undefined;
  const firstPass = civil - timezoneOffsetAt(civil, timezone);
  const corrected = civil - timezoneOffsetAt(firstPass, timezone);
  return new Date(corrected).toISOString();
}

/** Adapts the UI's date-only range to the API's UTC date-time boundaries. */
export function providerCalendarRange(range: CalendarDateRange, timezone: string): CalendarDateRange {
  const fromDate = range.from.slice(0, 10);
  const toDate = range.to.slice(0, 10);
  return {
    from: localDateTimeToIso(`${fromDate}T00:00`, timezone) ?? range.from,
    to: localDateTimeToIso(`${toDate}T00:00`, timezone) ?? range.to,
  };
}
